/**
 * CREATIVE DETONATOR - User Data Management
 * Handles user profile data loading and UI updates
 *
 * Extracted from dashboard/core.js for better modularity
 * @version 1.0
 */

import { authState } from '../../auth/js/state.js';
import { Storage } from './storage.js';
import { Sounds } from '../../../js/shared/sounds.js';

// ============================================================================
// USER DATA MODULE
// ============================================================================

export const UserData = {
  user: null,

  /**
   * Load user data from auth state (Supabase)
   * @returns {Object|null} User data object
   */
  async loadFromAuth() {
    if (authState.isLoggedIn) {
      this.user = {
        id: authState.user?.id,
        name: authState.displayName || 'User',
        email: authState.user?.email,
        avatar: authState.profile?.avatar_url,
        credits: authState.credits || 0,
        level: authState.profile?.level || 1,
        xp: authState.profile?.xp || 0,
        xpRequired: authState.profile?.xp_required || 1000,
        rank: authState.profile?.rank || 'STARTER',
        stats: {
          ideasGenerated: authState.profile?.ideas_generated || 0,
          ideasSaved: authState.profile?.ideas_saved || 0,
          imagesCreated: authState.profile?.images_created || 0,
          sessions: authState.profile?.sessions_count || 0,
          brands: authState.profile?.brands_count || 0,
          triggersUsed: authState.profile?.triggers_used || 0
        },
        preferences: {
          sounds: authState.profile?.pref_sounds !== false,
          animations: authState.profile?.pref_animations !== false,
          autoSave: authState.profile?.pref_auto_save || false,
          defaultIdeasCount: authState.profile?.pref_ideas_count || 3
        }
      };

      // Update UI with user info
      this.updateUI();
    }

    return this.user;
  },

  /**
   * Load user data from localStorage (legacy fallback)
   * @returns {Object} User data object
   */
  async load() {
    this.user = Storage.get('user', {
      id: 'user_1',
      name: 'Guest',
      email: 'guest@example.com',
      avatar: null,
      credits: 0,
      level: 1,
      xp: 0,
      xpRequired: 1000,
      rank: 'STARTER',
      stats: {
        ideasGenerated: 0,
        ideasSaved: 0,
        imagesCreated: 0,
        sessions: 0,
        brands: 0,
        triggersUsed: 0
      },
      preferences: {
        sounds: true,
        animations: true,
        autoSave: false,
        defaultIdeasCount: 3
      }
    });

    return this.user;
  },

  /**
   * Update UI elements with current user data
   */
  updateUI() {
    // Update user name displays (including profile widget)
    document.querySelectorAll('.user-name, .profile-name').forEach(el => {
      el.textContent = this.user?.name || 'User';
    });

    // Update user avatar (including profile widget)
    document.querySelectorAll('.user-avatar, .profile-avatar').forEach(el => {
      const name = this.user?.name || 'User';
      if (this.user?.avatar) {
        el.style.backgroundImage = `url(${this.user.avatar})`;
        el.style.backgroundSize = 'cover';
        el.style.backgroundPosition = 'center';
        el.textContent = '';
      } else {
        el.style.backgroundImage = '';
        el.textContent = name[0].toUpperCase();
      }
    });

    // Update credits displays (including profile widget)
    document.querySelectorAll('.credits-value, .header-credits-value').forEach(el => {
      el.textContent = this.user?.credits || 0;
    });

    // Update profile header name/role if present
    document.querySelectorAll('.profile-header-name').forEach(el => {
      el.textContent = this.user?.name || 'User';
    });
    document.querySelectorAll('.profile-header-role span').forEach(el => {
      el.textContent = this.user?.rank || 'Member';
    });

    // Update stats displays
    const stats = this.user?.stats || {};
    document.querySelectorAll('.stat-ideas').forEach(el => {
      el.textContent = stats.ideasGenerated || 0;
    });
    document.querySelectorAll('.stat-images').forEach(el => {
      el.textContent = stats.imagesCreated || 0;
    });
    document.querySelectorAll('.stat-saved').forEach(el => {
      el.textContent = stats.ideasSaved || 0;
    });
  },

  /**
   * Save user data to localStorage
   */
  save() {
    Storage.set('user', this.user);
  },

  /**
   * Update user credits
   * @param {number} amount - Amount to add (can be negative)
   * @deprecated Use credit-animation.js instead (animateCoinSpend, refreshCreditsFromServer)
   * This method is kept for backwards compatibility but should not be used for new code.
   * The centralized credit system in js/shared/credit-animation.js is the source of truth.
   */
  updateCredits(amount) {
    // CREDIT-FIX: Sync with authState to prevent desync
    if (typeof authState.credits === 'number') {
      this.user.credits = authState.credits + amount;
    } else {
      this.user.credits += amount;
    }
    this.save();

    // Update UI
    document.querySelectorAll('.credits-amount, #credits-balance').forEach(el => {
      el.textContent = this.user.credits;
    });

    // Play coin sound if adding credits
    if (amount > 0) {
      Sounds.play('coin');
    }
  },

  /**
   * Get current credits balance
   * @returns {number}
   */
  getCredits() {
    return this.user?.credits || 0;
  },

  /**
   * Get user stats
   * @returns {Object}
   */
  getStats() {
    return this.user?.stats || {};
  },

  /**
   * Get user preferences
   * @returns {Object}
   */
  getPreferences() {
    return this.user?.preferences || {};
  }
};
